import { LitElement, html, css, nothing } from 'lit';
import { generationChanged } from '../utils/constants.js';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import type { VerificationDefinition, RequirementDefinition } from '@arctek/sysml-types';

// ---------------------------------------------------------------------------
// <sysml-traceability-panel> -- Traceability matrix
//
// Ported from: SysmlTraceabilityMatrix.vue
//
// Features:
// - Matrix grid with color-coded cells (S=satisfied, P=partial, M=missing)
// - Filter controls (by linkage type: satisfy, verify, refine)
// - Coverage summary row per verification case
// - Overall coverage badge
// - Click-to-navigate (headers link to source)
// - Responsive sticky headers
// ---------------------------------------------------------------------------

type LinkType = 'all' | 'verify' | 'satisfy' | 'refine';

interface CellData {
  key: string;
  reqId: string;
  verId: string;
  status: 'satisfied' | 'partial' | 'missing' | 'na';
  linkType: 'verify' | 'satisfy' | 'refine' | null;
}

@customElement('sysml-traceability-panel')
export class SysmlTraceabilityPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; }

      /* -- Summary bar -- */
      .summary-bar {
        display: flex;
        align-items: center;
        gap: var(--r5-space-3);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        font-size: var(--r5-text-xs);
        flex-shrink: 0;
        flex-wrap: wrap;
      }
      .summary-stat {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
      }

      /* -- Filter controls -- */
      .filter-row {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        font-size: var(--r5-text-xs);
        flex-shrink: 0;
      }
      .filter-label {
        color: var(--r5-text-muted);
        font-weight: 500;
      }
      .filter-chip {
        padding: 1px var(--r5-space-2);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-full);
        background: var(--r5-surface);
        color: var(--r5-text-muted);
        font-size: var(--r5-font-size-badge);
        font-weight: 500;
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
      }
      .filter-chip:hover { border-color: var(--r5-primary); color: var(--r5-text); }
      .filter-chip.active {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
        color: var(--r5-primary);
      }

      /* -- Matrix table -- */
      .matrix-wrap {
        overflow: auto;
        flex: 1;
        padding: var(--r5-space-2);
      }
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--r5-text-xs);
      }
      th, td {
        padding: var(--r5-space-1) var(--r5-space-2);
        border: 1px solid var(--r5-border);
        text-align: center;
        white-space: nowrap;
        min-width: 60px;
      }
      th {
        background: var(--r5-surface-muted);
        font-weight: 600;
        position: sticky;
        top: 0;
        z-index: 1;
      }
      th.corner {
        position: sticky;
        top: 0;
        left: 0;
        z-index: 3;
        background: var(--r5-surface-muted);
      }
      th.row-header {
        text-align: left;
        position: sticky;
        left: 0;
        z-index: 2;
        background: var(--r5-surface-muted);
        max-width: 140px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* -- Cell colors -- */
      .cell-satisfied {
        background: var(--r5-success-50);
        color: var(--r5-success-700);
        font-weight: 600;
        cursor: pointer;
      }
      .cell-satisfied:hover { background: var(--r5-success-100); }
      .cell-partial {
        background: var(--r5-warning-50);
        color: var(--r5-warning-700);
        font-weight: 600;
        cursor: pointer;
      }
      .cell-partial:hover { background: var(--r5-warning-100); }
      .cell-missing {
        background: var(--r5-destructive-50);
        color: var(--r5-destructive-700);
        font-weight: 500;
      }
      .cell-na {
        background: var(--r5-surface);
        color: var(--r5-text-muted);
      }

      .clickable-header {
        cursor: pointer;
        text-decoration: underline;
        text-decoration-style: dotted;
        text-underline-offset: 2px;
      }
      .clickable-header:hover { color: var(--r5-primary); }

      /* -- Coverage row -- */
      .coverage-row td {
        font-weight: 600;
        background: var(--r5-surface-muted);
      }

      /* -- Legend -- */
      .legend {
        display: flex;
        gap: var(--r5-space-3);
        padding: var(--r5-space-1) var(--r5-space-3);
        border-top: 1px solid var(--r5-border);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        flex-shrink: 0;
      }
      .legend-item {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
      }
      .legend-swatch {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        border: 1px solid var(--r5-border);
      }
    `,
  ];

  // CRITICAL: hasChanged: generationChanged for controller properties
  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;

  @state() private _linkFilter: LinkType = 'all';

  override render() {
    const reqs = this.modeler.requirements;
    const verCases = this.modeler.verificationCases;

    if (reqs.length === 0 && verCases.length === 0) {
      return html`
        <div class="panel-header"><h3>Traceability Matrix</h3></div>
        <div class="panel-body">
          <div class="empty-state">
            <ark-icon name="grid" size="lg"></ark-icon>
            <div>Add requirements and verification cases to see the traceability matrix.</div>
          </div>
        </div>
      `;
    }

    const { matrix, cells } = this._buildMatrix(reqs, verCases);
    const coveragePct = this._computeCoverage(cells, reqs.length);

    return html`
      <div class="panel-header">
        <h3>Traceability Matrix</h3>
        <span class="badge ${coveragePct >= 80 ? 'badge-success' : coveragePct >= 50 ? 'badge-warning' : 'badge-error'}">
          ${coveragePct}% coverage
        </span>
      </div>

      <div class="summary-bar">
        <span class="summary-stat">
          <span style="font-weight: 600;">${reqs.length}</span> requirement${reqs.length !== 1 ? 's' : ''}
        </span>
        <span class="summary-stat">
          <span style="font-weight: 600;">${verCases.length}</span> verification case${verCases.length !== 1 ? 's' : ''}
        </span>
        <span class="summary-stat">
          <span style="font-weight: 600;">${cells.filter((c) => c.status === 'satisfied').length}</span> links
        </span>
      </div>

      ${this._renderFilterRow()}

      <div class="matrix-wrap">
        <table>
          <thead>
            <tr>
              <th class="corner">Req \\ Ver</th>
              ${verCases.map(
                (vc) => html`
                  <th class="clickable-header" @click=${() => this._onElementClick(vc)}
                    title=${vc.name}>
                    ${this._truncate(vc.name, 12)}
                  </th>
                `,
              )}
            </tr>
          </thead>
          <tbody>
            ${reqs.map((req) => this._renderRow(req, verCases, matrix))}
            ${this._renderCoverageRow(reqs, verCases, matrix)}
          </tbody>
        </table>
      </div>

      ${this._renderLegend()}
    `;
  }

  private _renderFilterRow() {
    const filters: { key: LinkType; label: string }[] = [
      { key: 'all', label: 'All Links' },
      { key: 'verify', label: 'Verify' },
      { key: 'satisfy', label: 'Satisfy' },
      { key: 'refine', label: 'Refine' },
    ];

    return html`
      <div class="filter-row">
        <span class="filter-label">Show:</span>
        ${filters.map(
          (f) => html`
            <button
              class="filter-chip ${this._linkFilter === f.key ? 'active' : ''}"
              @click=${() => (this._linkFilter = f.key)}
            >
              ${f.label}
            </button>
          `,
        )}
      </div>
    `;
  }

  private _renderRow(
    req: { id: string; name: string; source?: { startLine: number; startColumn: number; endLine: number; endColumn: number } },
    verCases: { id: string; name: string }[],
    matrix: Map<string, CellData>,
  ) {
    return html`
      <tr>
        <th class="row-header clickable-header" @click=${() => this._onElementClick(req)} title=${req.name}>
          ${this._truncate(req.name, 16)}
        </th>
        ${verCases.map((vc) => {
          const key = `${req.id}:${vc.id}`;
          const cell = matrix.get(key);
          const status = cell?.status ?? 'na';

          // Apply link filter
          if (this._linkFilter !== 'all' && cell?.linkType && cell.linkType !== this._linkFilter) {
            return html`<td class="cell-na">-</td>`;
          }

          const cls =
            status === 'satisfied' ? 'cell-satisfied'
            : status === 'partial' ? 'cell-partial'
            : status === 'missing' ? 'cell-missing'
            : 'cell-na';
          const label =
            status === 'satisfied' ? 'S'
            : status === 'partial' ? 'P'
            : status === 'missing' ? 'M'
            : '-';
          const linkSuffix = cell?.linkType ? ` (${cell.linkType})` : '';

          return html`<td class=${cls} title="${status}${linkSuffix}">${label}</td>`;
        })}
      </tr>
    `;
  }

  private _renderCoverageRow(
    reqs: { id: string }[],
    verCases: { id: string }[],
    matrix: Map<string, CellData>,
  ) {
    return html`
      <tr class="coverage-row">
        <td style="text-align: left; font-weight: 600;">Coverage</td>
        ${verCases.map((vc) => {
          const satisfied = reqs.filter(
            (req) => (matrix.get(`${req.id}:${vc.id}`)?.status ?? 'na') === 'satisfied',
          ).length;
          const pct = reqs.length > 0 ? Math.round((satisfied / reqs.length) * 100) : 0;
          return html`<td>${pct}%</td>`;
        })}
      </tr>
    `;
  }

  private _renderLegend() {
    return html`
      <div class="legend">
        <div class="legend-item">
          <span class="legend-swatch" style="background: var(--r5-success-50);"></span>
          S = Satisfied
        </div>
        <div class="legend-item">
          <span class="legend-swatch" style="background: var(--r5-warning-50);"></span>
          P = Partial
        </div>
        <div class="legend-item">
          <span class="legend-swatch" style="background: var(--r5-destructive-50);"></span>
          M = Missing
        </div>
        <div class="legend-item">
          <span class="legend-swatch" style="background: var(--r5-surface);"></span>
          - = N/A
        </div>
      </div>
    `;
  }

  private _buildMatrix(
    reqs: RequirementDefinition[],
    verCases: VerificationDefinition[],
  ): { matrix: Map<string, CellData>; cells: CellData[] } {
    const matrix = new Map<string, CellData>();
    const cells: CellData[] = [];

    for (const req of reqs) {
      for (const vc of verCases) {
        const key = `${req.id}:${vc.id}`;
        const subjectRef = typeof vc.subject?.ref === 'string' ? vc.subject.ref : undefined;

        let status: CellData['status'] = 'na';
        let linkType: CellData['linkType'] = null;

        if (subjectRef === req.name || subjectRef === req.id) {
          status = 'satisfied';
          linkType = 'verify';
        }

        const cell: CellData = { key, reqId: req.id, verId: vc.id, status, linkType };
        matrix.set(key, cell);
        if (status !== 'na') cells.push(cell);
      }
    }

    return { matrix, cells };
  }

  private _computeCoverage(cells: CellData[], reqCount: number): number {
    if (reqCount === 0) return 0;
    const satisfiedReqs = new Set<string>();
    for (const cell of cells) {
      if (cell.status === 'satisfied') {
        satisfiedReqs.add(cell.reqId);
      }
    }
    return Math.round((satisfiedReqs.size / reqCount) * 100);
  }

  private _onElementClick(el: { source?: { startLine: number; startColumn: number; endLine: number; endColumn: number } }): void {
    if (el.source) {
      this.modeler.revealInEditor(el.source);
    }
  }

  private _truncate(text: string, maxLen: number): string {
    return text.length > maxLen ? text.slice(0, maxLen - 1) + '\u2026' : text;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-traceability-panel': SysmlTraceabilityPanel;
  }
}
